import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys, type GenerationFilterParams } from '@/lib/query-keys';
import { generationsApi } from '../api';

const PAGE_SIZE = 24;

/** Filtered, cursor-paginated history. Each page also carries totals for the whole filtered set. */
export function useGenerations(filters: GenerationFilterParams) {
  return useInfiniteQuery({
    queryKey: queryKeys.generations.list(filters),
    queryFn: ({ pageParam, signal }) =>
      generationsApi.list({ ...filters, limit: PAGE_SIZE, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    placeholderData: (previous) => previous,
  });
}

/** All-time count, cost and tokens for this browser. */
export function useGenerationStats() {
  return useQuery({
    queryKey: queryKeys.generations.stats(),
    queryFn: ({ signal }) => generationsApi.stats(signal),
  });
}

export function useDeleteGeneration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: generationsApi.remove,
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.generations.all }),
  });
}

export function useClearGenerations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: generationsApi.clear,
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.generations.all }),
  });
}
